import React from 'react';

import renderExpandedListing from 'nav/expanded/RenderExpandedListing';
import BenchlingProfiler from 'performance/BenchlingProfiler';
import {composeSidepanelSearchResultsRenderedEvent} from 'performance/processSearchEvents';
import {ListableItem, SearchResults, Sort} from 'search/SearchConstants';
import {trackSelectedSearchResult} from 'search/searchTracking';
import ExpandedScrollableTable from 'ui-library/scrollable/ExpandedScrollableTable';
import {ScrollableTableRow, TABLE_KEYS} from 'ui-library/scrollable/ScrollableTableConstants';
import {cycleColumnHeaderSort} from 'ui-library/scrollable/ScrollableTableUtil';

import {WorkflowSummaryGraphQL} from '../../../graphql-fragments/workflows/__generated/WorkflowSummaryGraphQL';
import * as WorkflowActions from '../WorkflowActions';
import {WorkflowDashboardDetails} from '../WorkflowConstants';

interface WorkflowTableProps {
  workflows: Array<WorkflowSummaryGraphQL>;
  workflowIdToDashboardDetails: {[workflowId: string]: WorkflowDashboardDetails};
  emptyMessage: string;
  searchResults: SearchResults | null;
  sorts: Array<Sort>;
  onSetColumnHeaderSort: (sort: Sort) => void;
}

export default class WorkflowTable extends React.Component<WorkflowTableProps> {
  _handleOpenWorkflow = (row: ScrollableTableRow): void => {
    trackSelectedSearchResult(row.id, this.props.searchResults);
    WorkflowActions.openWorkflowDetailsScreen(row.id);
  };

  _handleSetSort = (sortKey: string): void => {
    const newSort = cycleColumnHeaderSort(this.props.sorts, sortKey);
    this.props.onSetColumnHeaderSort(newSort);
  };

  override render(): JSX.Element {
    // Since we aren't passing groups, we can ignore the group related return values safely.
    const {columns, rowDataByItemId, iconsByItemId} = renderExpandedListing(
      // This cast is because we didn't add permissions, which are required in ListableItem.
      this.props.workflows as Array<ListableItem>,
      this.props.sorts,
      {
        workflowIdToDashboardDetails: this.props.workflowIdToDashboardDetails,
      }
    );

    return (
      <BenchlingProfiler {...composeSidepanelSearchResultsRenderedEvent()}>
        <ExpandedScrollableTable
          canFilterColumns={true}
          className="u-flex-grow u-flex-full-height"
          matchParentHeight={true}
          columns={columns}
          onRowClick={this._handleOpenWorkflow}
          emptyTableText={this.props.emptyMessage}
          rows={this.props.workflows.map((workflow) => ({
            id: workflow.id,
            data: rowDataByItemId[workflow.id],
            icon: iconsByItemId[workflow.id],
          }))}
          isCompact={false}
          hasLineBelowHeader={false}
          tableKey={TABLE_KEYS.EXPANDED_WORKFLOW_TABLE}
          onChangeSort={this._handleSetSort}
        />
      </BenchlingProfiler>
    );
  }
}
